import { useEffect, useMemo, useState } from 'react';
import { Gauge } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import usePageMetadata from '../../hooks/usePageMetadata';
import { getGauge } from '../../services/toleranceService';
import { parseToleranceInput } from './toleranceDisplay';
import GaugeVisualization from './GaugeVisualization';
import './GaugePage.css';
import { useUnits } from '../../units/UnitContext';
import { formatUnitValue, unitLabel } from '../../units/unitConversions';
import { useUnitText } from '../../units/useUnitText';

const REQUEST_DELAY_MS = 250;
const PAGE_TITLE = 'DIN 7162 GO NO-GO Gauge Calculator | ASiJoLab';
const PAGE_DESCRIPTION =
  'Calculate DIN 7162 GO and NO-GO gauge dimensions, manufacturing tolerances and wear limits for ISO 286 holes and shafts.';

const GaugeResultCard = ({ title, values, format, dinHalfToleranceUm, unitSystem }) => {
  const dinHalfToleranceDisplay = unitSystem === 'inch'
    ? formatUnitValue(dinHalfToleranceUm, 'um', unitSystem)
    : (dinHalfToleranceUm / 1000).toFixed(5).replace(/0$/, '');

  return (
  <section className="gaugeResultCard" aria-labelledby={`gauge-${title.toLowerCase()}-title`}>
    <h2 id={`gauge-${title.toLowerCase()}-title`}>{title} — new gauge</h2>
    <div className="gaugeNewTarget">
      <strong>{format(values.centerMm)} {unitLabel('mm', unitSystem)}</strong>
      <span>±{dinHalfToleranceDisplay} {unitLabel('mm', unitSystem)} DIN</span>
    </div>
    <dl>
      <div><dt>DIN manufacturing range</dt><dd>{format(values.dinMinimumMm)} – {format(values.dinMaximumMm)} {unitLabel('mm', unitSystem)}</dd></div>
      <div><dt>Manufacturer achievable range</dt><dd>{format(values.practicalMinimumMm)} – {format(values.practicalMaximumMm)} {unitLabel('mm', unitSystem)}</dd></div>
      {values.wearLimitMm !== undefined && (
        <div><dt>Wear limit</dt><dd>{format(values.wearLimitMm)} {unitLabel('mm', unitSystem)}</dd></div>
      )}
    </dl>
  </section>
  );
};

const GaugePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { unitSystem } = useUnits();
  const initialDiameter = searchParams.get('diameter');
  const initialTolerance = searchParams.get('tolerance');
  const [inputValue, setInputValue] = useState(
    initialDiameter && initialTolerance ? `${initialDiameter}${initialTolerance}` : ''
  );
  const [capability, setCapability, canonicalCapability] = useUnitText(searchParams.get('manufacturingCapabilityUm') || '2.0', 'um', unitSystem);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const parsedInput = useMemo(() => parseToleranceInput(inputValue), [inputValue]);
  const capabilityNumber = canonicalCapability(capability);

  usePageMetadata(PAGE_TITLE, PAGE_DESCRIPTION);

  useEffect(() => {
    if (parsedInput.status !== 'valid' || !Number.isFinite(capabilityNumber) || capabilityNumber <= 0) {
      setLoading(false);
      setResult(null);
      if (parsedInput.status === 'invalid') {
        setMessage({ type: 'invalid', text: parsedInput.message });
      } else if (capability !== '' && (!Number.isFinite(capabilityNumber) || capabilityNumber <= 0)) {
        setMessage({ type: 'invalid', text: 'Manufacturing capability must be greater than zero.' });
      } else {
        setMessage(null);
      }
      return undefined;
    }

    const controller = new AbortController();
    let active = true;
    setLoading(true);
    setMessage(null);
    const timer = window.setTimeout(async () => {
      const tolerance = `${parsedInput.position}${parsedInput.grade}`;
      try {
        const loaded = await getGauge({
          diameter: parsedInput.diameter,
          tolerance,
          manufacturingCapabilityUm: capabilityNumber,
        }, { signal: controller.signal });
        if (!active) return;
        setResult(loaded);
        const next = {
          diameter: String(parsedInput.diameter),
          tolerance,
        };
        if (Math.abs(capabilityNumber - 2) > 1e-6) next.manufacturingCapabilityUm = String(capabilityNumber);
        setSearchParams(next, { replace: true });
      } catch (error) {
        if (!active || error?.code === 'ERR_CANCELED') return;
        setResult(null);
        if (error?.response?.status === 404) {
          setMessage({ type: 'notAvailable', text: 'DIN 7162 gauge data is not available for this tolerance.' });
        } else if (error?.response?.status === 400) {
          setMessage({ type: 'invalid', text: 'Check the diameter, designation, and manufacturing capability.' });
        } else {
          setMessage({ type: 'error', text: 'The gauge calculation could not be loaded. Please try again.' });
        }
      } finally {
        if (active) setLoading(false);
      }
    }, REQUEST_DELAY_MS);
    return () => {
      active = false;
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [parsedInput, capability, capabilityNumber, setSearchParams]);

  const detected = parsedInput.status === 'valid'
    ? parsedInput.type === 'hole' ? 'Hole · Plug gauge' : 'Shaft · Ring gauge'
    : null;
  const relevantValues = result
    ? [
        result.workpiece.minimumMm, result.workpiece.maximumMm,
        ...Object.values(result.go).filter((value) => typeof value === 'number'),
        ...Object.values(result.notGo).filter((value) => typeof value === 'number'),
      ]
    : [];
  const precision = relevantValues.some(
    (value) => Math.abs(value * 1000 - Math.round(value * 1000)) > 1e-8
  ) ? 4 : 3;
  const format = (value) => unitSystem === 'inch' ? formatUnitValue(value, 'mm', unitSystem) : Number(value).toFixed(precision);
  const symbol = result?.gaugeType === 'plug'
    ? { half: 'H/2', offset: 'z', wear: 'y', safety: 'α' }
    : { half: 'H1/2', offset: 'z1', wear: 'y1', safety: 'α1' };

  return (
    <main className="gaugePage">
      <header className="gaugeHeader">
        <p>Tolerances · DIN 7162</p>
        <h1>DIN 7162 Gauge Calculator</h1>
        <span>Calculate workshop GO and NO-GO gauge limits for an ISO 286 hole or shaft.</span>
      </header>

      <p className="gaugeIntro">
        Calculate or verify GO/NO-GO gauges and see how gauge manufacturing
        tolerance and wear relate to the workpiece limits.
      </p>

      <section className="gaugeCalculator">
        <label htmlFor="gauge-designation">Nominal size and tolerance designation</label>
        <input id="gauge-designation" type="text" value={inputValue}
          onChange={(event) => setInputValue(event.target.value)}
          placeholder="Enter e.g. 25H7" autoComplete="off" spellCheck="false" />
        <small>The number in a tolerance designation is always millimetres.</small>
        <p className="gaugeDetected" aria-live="polite">{detected || 'Uppercase selects a hole/plug gauge; lowercase selects a shaft/ring gauge.'}</p>
        <label htmlFor="gauge-capability">Practical manufacturing capability</label>
        <div className="gaugeCapabilityInput">
          <span aria-hidden="true">±</span>
          <input id="gauge-capability" type="number" min="0" step={unitSystem === 'inch' ? 'any' : '0.1'}
            value={capability} onChange={(event) => setCapability(event.target.value)} />
          <span>{unitLabel('um', unitSystem)}</span>
        </div>
        <p className="gaugeHelp">The default reflects a practical gauge-grinding capability and can be adjusted for your manufacturer.</p>
        {message && <p className={`gaugeMessage gaugeMessage--${message.type}`} role={message.type === 'error' ? 'alert' : 'status'}>{message.text}</p>}
      </section>

      <section className="gaugeResultArea" aria-live="polite" aria-busy={loading}>
        {loading && <p className="gaugeLoading">Calculating gauge…</p>}
        {result && (
          <article className="gaugeResult">
            <header className="gaugeResultHeader">
              <span aria-hidden="true"><Gauge size={24} /></span>
              <div>
                <h2>{unitSystem === 'inch' ? `${format(result.diameterMm)} ${unitLabel('mm', unitSystem)}` : result.diameterMm} {result.designation}</h2>
                <p>{result.workpieceType === 'hole' ? 'Hole' : 'Shaft'} · {result.gaugeType === 'plug' ? 'Plug Gauge' : 'Ring Gauge'}</p>
                <strong>Workpiece: {format(result.workpiece.minimumMm)} – {format(result.workpiece.maximumMm)} {unitLabel('mm', unitSystem)}</strong>
              </div>
            </header>

            <div className="gaugePrimaryResults">
              <GaugeResultCard title="GO" values={result.go} format={format} dinHalfToleranceUm={result.manufacturing.dinHalfToleranceUm} unitSystem={unitSystem} />
              <GaugeResultCard title="NO-GO" values={result.notGo} format={format} dinHalfToleranceUm={result.manufacturing.dinHalfToleranceUm} unitSystem={unitSystem} />
            </div>

            <GaugeVisualization result={result} />

            <section className="gaugeMeaning" aria-labelledby="gauge-meaning-title">
              <h2 id="gauge-meaning-title">What does this mean for {unitSystem === 'inch' ? `${format(result.diameterMm)} ${unitLabel('mm', unitSystem)}` : result.diameterMm} {result.designation}?</h2>
              {result.workpieceType === 'hole' ? (
                <p>
                  The workpiece may be as small as {format(result.workpiece.minimumMm)} {unitLabel('mm', unitSystem)},
                  while the DIN manufacturing zone of a new GO plug gauge is{' '}
                  {format(result.go.dinMinimumMm)}–{format(result.go.dinMaximumMm)} {unitLabel('mm', unitSystem)}.
                  A GO/NO-GO gauge does not report the actual hole size; it checks the
                  part against defined limit conditions while accounting for gauge
                  manufacturing tolerance and wear.
                </p>
              ) : (
                <p>
                  The workpiece may be as large as {format(result.workpiece.maximumMm)} {unitLabel('mm', unitSystem)},
                  while the DIN manufacturing zone of a new GO ring gauge is{' '}
                  {format(result.go.dinMinimumMm)}–{format(result.go.dinMaximumMm)} {unitLabel('mm', unitSystem)}.
                  A GO/NO-GO ring gauge does not report the actual shaft size; it checks
                  the part against defined limit conditions while accounting for gauge
                  manufacturing tolerance and wear.
                </p>
              )}
            </section>

            <section className="gaugeManufacturing" aria-labelledby="gauge-manufacturing-title">
              <h2 id="gauge-manufacturing-title">Manufacturing tolerance</h2>
              <dl>
                <div><dt>DIN 7162 gauge tolerance</dt><dd>±{formatUnitValue(result.manufacturing.dinHalfToleranceUm, 'um', unitSystem)} {unitLabel('um', unitSystem)}</dd></div>
                <div><dt>Practical manufacturing capability</dt><dd>±{formatUnitValue(result.manufacturing.manufacturingCapabilityUm, 'um', unitSystem)} {unitLabel('um', unitSystem)}</dd></div>
                <div><dt>Achievable shop tolerance</dt><dd>±{formatUnitValue(result.manufacturing.appliedHalfToleranceUm, 'um', unitSystem)} {unitLabel('um', unitSystem)}</dd></div>
              </dl>
              {result.manufacturing.manufacturingCapabilityExceedsDin && (
                <p className="gaugeCapabilityNotice">This manufacturing capability is wider than the DIN 7162 tolerance. DIN 7162 conformity cannot be guaranteed with this capability.</p>
              )}
            </section>

            <details className="gaugeDinData">
              <summary>DIN 7162 calculation data</summary>
              <dl>
                <div><dt>Gauge manufacturing tolerance</dt><dd>{symbol.half} = ±{formatUnitValue(result.dinData.halfGaugeToleranceUm, 'um', unitSystem)} {unitLabel('um', unitSystem)}</dd></div>
                <div><dt>GO design offset</dt><dd>{symbol.offset} = {formatUnitValue(result.dinData.designOffsetUm, 'um', unitSystem)} {unitLabel('um', unitSystem)}</dd></div>
                <div><dt>Wear allowance</dt><dd>{symbol.wear} = {formatUnitValue(result.dinData.wearAllowanceUm, 'um', unitSystem)} {unitLabel('um', unitSystem)}</dd></div>
                <div><dt>Metrology safety allowance</dt><dd>{symbol.safety} = {formatUnitValue(result.dinData.metrologySafetyAllowanceUm, 'um', unitSystem)} {unitLabel('um', unitSystem)}</dd></div>
                <div><dt>DIN tolerance selection</dt><dd>{result.dinData.toleranceMatch === 'exact' ? 'Exact' : 'Next larger'} · {formatUnitValue(result.dinData.workpieceToleranceUm, 'um', unitSystem)} {unitLabel('um', unitSystem)}</dd></div>
              </dl>
            </details>
          </article>
        )}
      </section>

      <section className="gaugeExplanation" aria-labelledby="gauge-explanation-title">
        <h2 id="gauge-explanation-title">Why doesn't the gauge exactly match the workpiece tolerance?</h2>
        <p>A GO/NO-GO gauge is a limit-checking tool, not a dimensional measuring instrument. The gauge itself must also be manufactured within a tolerance and may include allowances for wear and metrological safety. Its usable limits therefore do not simply coincide with the minimum and maximum workpiece dimensions.</p>
      </section>

      <details className="gaugeEducation">
        <summary>
          <span>Why use the Gauge Calculator?</span>
          <small>Inspection, gauge manufacture, and the limits of GO/NO-GO gauging</small>
        </summary>
        <div className="gaugeEducationContent">
          <article>
            <h2>Check an existing gauge</h2>
            <p>Compare an existing GO/NO-GO gauge with the calculated DIN dimensions and confirm that the GO gauge remains within its wear limit. A worn or incorrectly manufactured gauge cannot be relied upon for the intended limit-gauging decision.</p>
          </article>
          <article>
            <h2>Make your own gauge</h2>
            <p>Use the target dimensions and manufacturing tolerance as the dimensional basis for making a workshop gauge. Calculation alone does not make the finished gauge calibrated, certified, or metrologically traceable.</p>
          </article>
          <article>
            <h2>Understand what the gauge tells you</h2>
            <p><strong>GO/NO-GO gauges verify conformity — they do not measure size.</strong> If you need the actual diameter, use an appropriate measuring instrument.</p>
          </article>
          <aside className="gaugeEducationExample" aria-label="Limit gauging example">
            <h2>Practical example · Ø25 H7</h2>
            <p>Allowed hole: <strong>{unitSystem === 'inch' ? `${formatUnitValue(25, 'mm', unitSystem)}–${formatUnitValue(25.021, 'mm', unitSystem)} ${unitLabel('mm', unitSystem)}` : '25.000–25.021 mm'}</strong></p>
            <p>A plug gauge can determine whether the hole satisfies the defined limit conditions, subject to the gauge itself being correctly manufactured and within its wear limit. It cannot report whether the actual hole is {formatUnitValue(25.003, 'mm', unitSystem)} {unitLabel('mm', unitSystem)}, {formatUnitValue(25.011, 'mm', unitSystem)} {unitLabel('mm', unitSystem)}, or {formatUnitValue(25.019, 'mm', unitSystem)} {unitLabel('mm', unitSystem)}. All three sizes are within the workpiece limits, but identifying the actual size requires measurement.</p>
          </aside>
          <p className="gaugeEducationNote">The calculator exposes DIN gauge dimensions, manufacturing tolerance, achievable manufacturing range, and the GO wear limit so the gauge's manufacture and condition can be considered alongside the workpiece limits.</p>
        </div>
      </details>
    </main>
  );
};

export default GaugePage;
